import { useCallback, useState } from "react";
import { useInfoMessage } from "hooks";
import usePreferences from "hooks/usePreferences";
import OneRepMaxService from "services/OneRepMaxService";
import UnitHelper from "helpers/UnitHelper";
import { useTranslation } from "react-i18next";
import type { ValidationResult } from "../useOneRepMaxValidator";

function useBulkOneRepMaxUpload() {
  const [loading, setLoading] = useState(false);
  const successMessage = useInfoMessage({ type: "success" });
  const errorMessage = useInfoMessage({ type: "error" });
  const [{ imperialSystemEnabled }] = usePreferences();
  const { t } = useTranslation();

  const uploadOneRepMaxes = useCallback(
    async (validationResult: ValidationResult) => {
      setLoading(true);

      try {
        // Transform data for API and filter out invalid weights
        const payload = validationResult.validData
          .map((item) => ({
            athleteId: item.athleteId,
            oneRepMaxes: item.oneRepMaxes
              .filter((orm) => orm.weight > 0) // Safety filter: remove weight values <= 0
              .map((orm) => ({
                exerciseId: orm.exerciseId,
                variant: orm.variant,
                // Convert LBS to KG if imperial system enabled
                weight: imperialSystemEnabled
                  ? UnitHelper.convert(orm.weight, UnitHelper.units.Pound, UnitHelper.units.Kilogram)
                  : orm.weight,
              })),
          }))
          .filter((item) => item.oneRepMaxes.length > 0); // Remove athletes with no valid 1RMs

        // Call API
        await OneRepMaxService.updateByAthleteIds(payload);

        // Show success message
        successMessage({
          message: t("athlete.bulk_1rm_upload_success", {
            athleteCount: validationResult.validAthleteCount,
            exerciseCount: validationResult.exerciseCount,
          }),
        });

        return true;
      } catch (error) {
        errorMessage({
          message: t("athlete.bulk_1rm_upload_error"),
        });
        return false;
      } finally {
        setLoading(false);
      }
    },
    [imperialSystemEnabled, successMessage, errorMessage, t]
  );

  return { uploadOneRepMaxes, loading };
}

export default useBulkOneRepMaxUpload;
